import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { 
  Flower, Home, Calendar, CalendarPlus, Wallet, Shop, 
  Group, CheckCircle, Menu, User, LogOut, Settings, TableRows, Clock, Sparks
} from 'iconoir-react';

const navItems = [
  { path: '/dashboard', icon: Home, label: 'Dashboard' },
  { path: '/events', icon: Calendar, label: 'Events' },
  { path: '/timeline', icon: Clock, label: 'Timeline' },
  { path: '/calendar', icon: CalendarPlus, label: 'Calendar' },
  { path: '/budget', icon: Wallet, label: 'Budget' },
  { path: '/vendors', icon: Shop, label: 'Vendors' },
  { path: '/guests', icon: Group, label: 'Guests' },
  { path: '/seating', icon: TableRows, label: 'Seating' },
  { path: '/checklist', icon: CheckCircle, label: 'Checklist' },
];

const adminNavItem = { path: '/admin', icon: Sparks, label: 'Admin' };

// Turn an ISO timestamp into "today", "yesterday", "3 days ago",
// "last Tuesday", or "Feb 19" for the profile dropdown.
function fmtSignedIn(iso) {
  if (!iso) return '';
  const then = new Date(iso);
  const now = new Date();
  const diffMs = now - then;
  const days = Math.floor(diffMs / 86400000);
  if (days < 1) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days} days ago`;
  if (days < 30) return `${Math.floor(days / 7)} wk ago`;
  return then.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-page">
      {/* Navigation */}
      <nav className="glass-nav sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/dashboard" className="flex items-center gap-2" data-testid="nav-logo">
              <Flower className="w-6 h-6 text-terracotta" strokeWidth={1.5} />
              <span className="font-brand text-xl text-charcoal hidden sm:block">Shaadi Planner</span>
            </Link>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-1">
              {[...navItems, ...(user?.role === 'admin' ? [adminNavItem] : [])].map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                const isAdmin = item.path === '/admin';
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex items-center gap-2 px-4 py-2 rounded-sm text-sm transition-colors ${
                      isActive 
                        ? 'text-charcoal bg-white/50' 
                        : isAdmin
                          ? 'text-terracotta hover:bg-white/30'
                          : 'text-muted hover:text-charcoal hover:bg-white/30'
                    }`}
                    data-testid={`nav-${item.label.toLowerCase()}`}
                  >
                    <Icon className="w-4 h-4" strokeWidth={1.5} />
                    {item.label}
                  </Link>
                );
              })}
            </div>

            {/* Right side */}
            <div className="flex items-center gap-4">
              {/* User Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    className="h-9 w-9 rounded-full bg-terracotta/10 text-terracotta hover:bg-terracotta/20"
                    data-testid="user-menu-btn"
                  >
                    <User className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 bg-white">
                  <div className="px-3 py-2">
                    <p className="text-sm font-medium text-charcoal">{user?.name}</p>
                    <p className="text-xs text-muted truncate">{user?.email}</p>
                    {user?.signed_in_since && (
                      <p
                        className="text-[10px] font-mono uppercase tracking-wider text-muted/80 mt-1.5"
                        data-testid="signed-in-since"
                      >
                        Signed in {fmtSignedIn(user.signed_in_since)}
                        {user.signed_in_remember && <span className="ml-1 text-moss">· 30d</span>}
                      </p>
                    )}
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/settings" className="flex items-center gap-2" data-testid="settings-link">
                      <Settings className="w-4 h-4" />
                      Settings
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={handleLogout}
                    className="flex items-center gap-2 text-terracotta focus:text-terracotta"
                    data-testid="logout-btn"
                  >
                    <LogOut className="w-4 h-4" />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Mobile menu button */}
              <Button
                variant="ghost"
                className="md:hidden h-9 w-9"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                data-testid="mobile-menu-btn"
              >
                <Menu className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>

        {/* Mobile Nav */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-white/40 bg-white/90 backdrop-blur-xl">
            <div className="px-4 py-3 space-y-1">
              {[...navItems, ...(user?.role === 'admin' ? [adminNavItem] : [])].map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                const isAdmin = item.path === '/admin';
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-sm text-sm transition-colors ${
                      isActive
                        ? 'text-charcoal bg-white/50'
                        : isAdmin
                          ? 'text-terracotta hover:bg-white/30'
                          : 'text-muted hover:text-charcoal hover:bg-white/30'
                    }`}
                    data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                  >
                    <Icon className="w-4 h-4" strokeWidth={1.5} />
                    {item.label}
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {children}
      </main>
    </div>
  );
}
